import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { hasAdFreeExperience } from '../lib/coachAccess';
import {
  pathnameToAffiliateContext,
  type AffiliatePageContext,
  type AffiliatePlacementSlot,
} from '../lib/affiliateContext';

export const AFFILIATE_ROTATION_MS = 10 * 60 * 1000;

type AffiliateOffer = {
  id: string;
  title: string;
  blurb: string;
  url: string;
  disclosure: string;
};

type AffiliateBundleResponse = {
  showAds: boolean;
  inline: AffiliateOffer | null;
  footer: AffiliateOffer | null;
  rotationKey: number;
  disclosure: string;
};

type AffiliatePageContextValue = {
  context: AffiliatePageContext | null;
  inline: AffiliateOffer | null;
  footer: AffiliateOffer | null;
  disclosure: string | null;
  rotationKey: number;
};

const AffiliatePageContext = createContext<AffiliatePageContextValue>({
  context: null,
  inline: null,
  footer: null,
  disclosure: null,
  rotationKey: 0,
});

export function AffiliatePageProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const context = useMemo(() => pathnameToAffiliateContext(pathname), [pathname]);
  const [inline, setInline] = useState<AffiliateOffer | null>(null);
  const [footer, setFooter] = useState<AffiliateOffer | null>(null);
  const [disclosure, setDisclosure] = useState<string | null>(null);
  const [rotationKey, setRotationKey] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), AFFILIATE_ROTATION_MS);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    if (!user || hasAdFreeExperience(user) || !context) {
      setInline(null);
      setFooter(null);
      setDisclosure(null);
      return;
    }
    try {
      const res = await api<AffiliateBundleResponse>(
        `/api/affiliates/for-page?context=${encodeURIComponent(context)}`,
      );
      if (!res.showAds) {
        setInline(null);
        setFooter(null);
        return;
      }
      setInline(res.inline);
      setFooter(res.footer);
      setDisclosure(res.disclosure);
      setRotationKey(res.rotationKey);
    } catch {
      setInline(null);
      setFooter(null);
    }
  }, [user, context, user?.adFreeEnabled, user?.aiCoachingEnabled, user?.coachAccessUnlocked]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  const value = useMemo(
    () => ({ context, inline, footer, disclosure, rotationKey }),
    [context, inline, footer, disclosure, rotationKey],
  );

  return <AffiliatePageContext.Provider value={value}>{children}</AffiliatePageContext.Provider>;
}

export function useAffiliatePageOffer(slot: AffiliatePlacementSlot) {
  const { inline, footer, disclosure } = useContext(AffiliatePageContext);
  const offer = slot === 'inline' ? inline : footer;
  return { offer, disclosure };
}
